import { CreditCard, ExternalLink, Lock, Sparkles, TriangleAlert } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router';
import { useAuth } from '../lib/auth';
import { ADD_ON_CONSEQUENCE, FEATURE_LABEL, formatAmount, SALES_EMAIL, usePlan, type BillingFeature, type PaymentIssue } from '../lib/billing';
import { formatDate } from '../lib/format';
import { Alert, Badge, Button, EmptyState, Modal } from './ui';

/** In place of a page while the school's Edvora plan has lapsed. Administrators are sent to renew it. */
export function PlanRequired() {
  const { me, can } = useAuth();
  const plan = usePlan();
  const basis = me?.tenant?.billing.basis;
  const unpaid = plan.paymentIssues.find((issue) => issue.item === 'PLATFORM' && issue.lapsed);
  // The banner above the page says what's owed and how to pay it.
  if (can('ADMIN') && unpaid) {
    return (
      <EmptyState
        icon={<Lock />}
        title="This page is paused"
        description={
          unpaid.status === 'CANCELED'
            ? 'It opens again as soon as you subscribe again.'
            : 'It opens again as soon as the overdue renewal is paid.'
        }
      />
    );
  }
  if (can('ADMIN')) {
    return (
      <EmptyState
        icon={<CreditCard />}
        title={plan.deletesAt ? 'Your free trial has ended' : 'Your school’s Edvora subscription isn’t active'}
        description={
          plan.deletesAt ? (
            <>
              Subscribe to keep using Edvora. Your school’s data is all still here until <strong>{formatDate(plan.deletesAt)}</strong>, when the
              account and its data are deleted. Need more time? Email{' '}
              <a href={`mailto:${SALES_EMAIL}`} className="font-medium underline">
                {SALES_EMAIL}
              </a>
              .
            </>
          ) : basis === 'NONE' ? (
            'Subscribe to Edvora, or renew your subscription, to keep using it. Your school’s data is all still here.'
          ) : (
            'Renew your subscription to keep using Edvora.'
          )
        }
        action={
          <Link to="/billing">
            <Button variant="primary" icon={<CreditCard />}>
              Plan & billing
            </Button>
          </Link>
        }
      />
    );
  }
  return (
    <EmptyState
      icon={<Lock />}
      title="Edvora isn’t available right now"
      description={`${me?.tenant?.name ?? 'Your school'}’s Edvora subscription isn’t active. Ask a school administrator to renew it.`}
    />
  );
}

const DAY = 86_400_000;

/** Above every page during a school's own free trial: how long is left, and what happens after. For staff. */
export function TrialBanner() {
  const { can } = useAuth();
  const plan = usePlan();
  const { pathname } = useLocation();
  // Plan & billing says all of this itself.
  if (!plan.enforced || !plan.signUpTrial || !plan.trialEndsAt || !can('TECHNICIAN') || pathname === '/billing') return null;
  const days = Math.max(0, Math.ceil((new Date(plan.trialEndsAt).getTime() - Date.now()) / DAY));
  return (
    <div className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-brand-200 bg-brand-50 px-4 py-2.5 text-sm text-brand-950 print:hidden">
      <p className="flex items-center gap-2">
        <Sparkles className="size-4 shrink-0 text-brand-600" />
        <span>
          <strong>Free trial:</strong> {days === 0 ? 'ends today' : `${days} day${days === 1 ? '' : 's'} left`} (through {formatDate(plan.trialEndsAt)}).
          Rosters and devices sync when you click Sync now; help desk email is checked automatically.
          {plan.deletesAt && (
            <span className="ml-1 inline-flex items-center gap-1 text-amber-800">
              <TriangleAlert className="size-3.5" /> Without a plan, the school is deleted on {formatDate(plan.deletesAt)}.
            </span>
          )}
        </span>
      </p>
      {can('ADMIN') && (
        <Link to="/billing">
          <Button size="sm" variant="primary" icon={<CreditCard />}>
            Choose a plan
          </Button>
        </Link>
      )}
    </div>
  );
}

const amountOf = (issue: PaymentIssue) => (issue.amountDue ? formatAmount(issue.amountDue) : 'the amount due');

/** What to tell an administrator about an unpaid renewal. */
export function paymentIssueText(issue: PaymentIssue, schoolName: string) {
  const date = formatDate(issue.accessEndsAt);
  if (issue.item !== 'PLATFORM') {
    const name = FEATURE_LABEL[issue.item];
    const what = ADD_ON_CONSEQUENCE[issue.item];
    // Once it has ended for good, the server stops listing it.
    if (issue.status === 'CANCELED') {
      return { title: `Your ${name} subscription ended`, body: `Its yearly renewal wasn’t paid. Add it again by ${date} to keep ${what}.` };
    }
    return issue.lapsed
      ? {
          title: `${name} is unpaid`,
          body: `Its yearly renewal hasn’t been paid, so ${what} is off until it’s renewed. Pay ${amountOf(issue)} to turn it back on.`,
        }
      : {
          title: `Your ${name} renewal didn’t go through`,
          body: `Pay ${amountOf(issue)} by ${date} to keep ${what}.`,
        };
  }
  if (issue.lapsed) {
    return {
      title: 'Your Edvora subscription is unpaid',
      body:
        issue.status === 'CANCELED'
          ? `${schoolName} lost access to Edvora on ${date}, because its renewal wasn’t paid. Subscribe again to restore it; everything is still here.`
          : `${schoolName} lost access to Edvora on ${date}, because its renewal wasn’t paid. Pay ${amountOf(issue)} to restore it; everything is still here.`,
    };
  }
  return {
    title: 'Your Edvora payment didn’t go through',
    body:
      issue.status === 'CANCELED'
        ? `${schoolName}’s subscription ended because its renewal wasn’t paid. Subscribe again by ${date}, or ${schoolName} loses access to Edvora until it’s renewed.`
        : `Update your payment method or pay the outstanding ${amountOf(issue)} by ${date}. After that, ${schoolName} loses access to Edvora until the subscription is renewed.`,
  };
}

/** Pay the invoice on Stripe's page, or go to Plan & billing for cards (or to subscribe again). `onBillingPage` leaves out that link. */
export function PaymentActions({ issue, compact, onBillingPage }: { issue: PaymentIssue; compact?: boolean; onBillingPage?: boolean }) {
  if (onBillingPage && !issue.invoiceUrl) return null;
  return (
    <span className="inline-flex flex-wrap items-center justify-center gap-2">
      {issue.invoiceUrl && (
        <a href={issue.invoiceUrl} target="_blank" rel="noopener noreferrer">
          <Button variant="primary" size={compact ? 'sm' : 'md'} icon={<ExternalLink />}>
            Pay {issue.amountDue ? formatAmount(issue.amountDue) : 'now'}
          </Button>
        </a>
      )}
      {!onBillingPage && (
        <Link to="/billing">
          <Button size={compact ? 'sm' : 'md'} icon={<CreditCard />}>
            {issue.status !== 'CANCELED' ? 'Update payment method' : issue.item === 'PLATFORM' ? 'Subscribe again' : 'Add it again'}
          </Button>
        </Link>
      )}
    </span>
  );
}

/** For administrators while a renewal is unpaid: above every page, and once each session as they sign in. */
export function PaymentDueBanner() {
  const { me, can } = useAuth();
  const plan = usePlan();
  const { pathname } = useLocation();
  const issues = plan.paymentIssues;
  const first = issues[0];
  const seenKey = first && me?.tenant ? `ec:payment-warning:${me.tenant.id}:${first.item}:${first.dueSince}` : null;
  const [dialog, setDialog] = useState(false);

  useEffect(() => {
    if (!seenKey || !can('ADMIN') || pathname === '/billing') return;
    try {
      if (sessionStorage.getItem(seenKey)) return;
      sessionStorage.setItem(seenKey, '1');
    } catch {
      // Storage unavailable: the banner still shows.
    }
    setDialog(true);
  }, [seenKey, can, pathname]);

  // Plan & billing lists these itself.
  if (!first || !can('ADMIN') || pathname === '/billing') return null;
  const schoolName = me?.tenant?.name ?? 'Your school';
  return (
    <>
      {issues.map((issue) => {
        const text = paymentIssueText(issue, schoolName);
        return (
          <div key={issue.item} role="alert" className="mb-6 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-950 print:hidden">
            <p className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 size-4 shrink-0 text-red-600" />
              <span>
                <strong>{text.title}.</strong> {text.body}
              </span>
            </p>
            <PaymentActions issue={issue} compact />
          </div>
        );
      })}
      <Modal
        open={dialog}
        onClose={() => setDialog(false)}
        title={paymentIssueText(first, schoolName).title}
        footer={
          <>
            <Button onClick={() => setDialog(false)}>Remind me later</Button>
            <PaymentActions issue={first} />
          </>
        }
      >
        <p className="text-sm text-slate-600">{paymentIssueText(first, schoolName).body}</p>
      </Modal>
    </>
  );
}

/** In place of a page that belongs to an add-on the school's plan doesn't include. */
export function AddOnRequired({ feature, description }: { feature: BillingFeature; description: ReactNode }) {
  const { can } = useAuth();
  return (
    <EmptyState
      icon={<Sparkles />}
      title={`${FEATURE_LABEL[feature]} is an add-on`}
      description={
        <>
          {description} {can('ADMIN') ? 'Add it to your school’s Edvora plan to get started.' : 'Ask a school administrator to add it to your Edvora plan.'}
        </>
      }
      action={
        can('ADMIN') && (
          <Link to="/billing">
            <Button variant="primary" icon={<CreditCard />}>
              Plan & billing
            </Button>
          </Link>
        )
      }
    />
  );
}

/** Marks an add-on the school's plan doesn't include. */
export function AddOnBadge({ feature, className }: { feature: BillingFeature; className?: string }) {
  const plan = usePlan();
  if (!plan.enforced || plan.has(feature)) return null;
  return (
    <Badge tone="violet" className={className}>
      <Sparkles className="size-3" /> Add-on
    </Badge>
  );
}

/** Explains an add-on the school's plan doesn't include, and where to add it. */
export function AddOnNotice({ feature, children, className }: { feature: BillingFeature; children?: ReactNode; className?: string }) {
  const plan = usePlan();
  const { can } = useAuth();
  if (!plan.enforced || plan.has(feature)) return null;
  return (
    <Alert tone="blue" icon={<Sparkles />} title={`${FEATURE_LABEL[feature]} is an add-on`} className={className}>
      {children}{' '}
      {can('ADMIN') ? (
        <Link to="/billing" className="font-medium underline">
          Add it under Plan & billing.
        </Link>
      ) : (
        'Ask a school administrator to add it to your Edvora plan.'
      )}
    </Alert>
  );
}
